import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Auth } from "aws-amplify";
import axios from "axios";
import moment from "moment";
import DashboardLayout from "../Layout/DashboardLayout";
import DashboardTopbar from "../Layout/DashboardTopbar";
import useTokenBalance from "../hooks/useTokenBalance";
import { useAuthContext } from "../Context/AuthContext";
import { useAlertContext } from "../Context/AlertContext";
import { API_R3API_ODOO_EXECUTE_URI } from "../Context/Constants";
import TokenPaymentPanel from "./GetToken/TokenPaymentPanel";
import TokenAllPlans from "./GetToken/TokenAllPlans";
import SubscriptionStatusCard from "./Subscription/SubscriptionStatusCard";
import SubscriptionOrderDetails from "./Subscription/SubscriptionOrderDetails";
import SubscriptionProductSelect from "./Subscription/SubscriptionProductSelect";
import {
  getPersistedSubscriptionUiState,
  saveSubscriptionPersistence
} from "../utils/subscriptionPersistence";
import {
  canPurchaseSubscription,
  STATUS_ACTIVE,
  STATUS_ERROR
} from "../utils/subscriptionValidityUtils";
import { useSubscriptionAccess } from "../Context/SubscriptionAccessContext";
import {
  extractProducts,
  findProductById,
  getProductTokenCount,
  normalizeProductId,
  readNumericValue
} from "../utils/getTokenProductUtils";
import "./GetToken/get-token.css";

const SUBSCRIPTION_CATEGORY = "Subscriptions";

const Subscription = () => {
  const persistedUi = getPersistedSubscriptionUiState();
  const { user } = useAuthContext();
  const { setSuccess, setError } = useAlertContext();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { tokenBalance } = useTokenBalance();
  const {
    status: validityStatus,
    checking: validityChecking,
    expirationDate,
    validityError
  } = useSubscriptionAccess();

  const [selectedProduct, setSelectedProduct] = useState(persistedUi.selectedProduct);
  const [loading, setLoading] = useState(!persistedUi.hasCache);
  const [refreshing, setRefreshing] = useState(false);
  const [productTax, setProductTax] = useState(0);
  const [includedTokens, setIncludedTokens] = useState(0);
  const [total, setTotal] = useState(0);
  const [productPrice, setProductPrice] = useState(0);
  const [totalAmountWithoutTax, setTotalAmountWithoutTax] = useState(0);
  const [paypalResponse, setPaypalResponse] = useState(null);
  const [products, setProducts] = useState(persistedUi.products);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const productsRef = useRef(persistedUi.products);
  const selectedProductIdRef = useRef(persistedUi.selectedProduct?.id ?? null);
  const userTouchedProductRef = useRef(false);

  productsRef.current = products;

  const applySelectedProductPricing = (product) => {
    if (!product) {
      setProductPrice("0.00");
      setIncludedTokens(0);
      setTotal(0);
      setTotalAmountWithoutTax(0);
      setProductTax(0);
      return;
    }

    const price = readNumericValue(product.list_price, 0);
    const taxRate = readNumericValue(product.tax_amount, 0) / 100;
    const tokens = getProductTokenCount(product);
    const subtotal = price;
    const taxAmount = subtotal * taxRate;
    const calculatedTotal = subtotal + taxAmount;

    setProductPrice(price.toFixed(2));
    setIncludedTokens(tokens);
    setTotalAmountWithoutTax(subtotal.toFixed(2));
    setProductTax(taxAmount.toFixed(2));
    setTotal(calculatedTotal.toFixed(2));
  };

  useEffect(() => {
    selectedProductIdRef.current = selectedProduct?.id ?? null;
  }, [selectedProduct]);

  useEffect(() => {
    if (persistedUi.selectedProduct) {
      applySelectedProductPricing(persistedUi.selectedProduct);
    }
  }, []);

  useEffect(() => {
    saveSubscriptionPersistence({
      selectedProductId: selectedProduct?.id ?? null,
      products
    });
  }, [selectedProduct, products]);

  const getAuthRequestContext = async () => {
    const session = await Auth.currentSession();
    const idTokenTrans = session.getIdToken().getJwtToken();
    const u = await Auth.currentAuthenticatedUser();
    return {
      email: u.attributes.email,
      headers: {
        Authorization: `Bearer ${idTokenTrans}`,
        "Content-Type": "application/json"
      }
    };
  };

  const sendTransactionDetails = async (paypalOrder) => {
    if (
      !selectedProduct?.id ||
      paymentBusy ||
      validityChecking ||
      !canPurchaseSubscription(validityStatus)
    ) {
      return;
    }

    setPaymentBusy(true);
    try {
      const { email, headers } = await getAuthRequestContext();
      const expirationTimeframe = readNumericValue(
        selectedProduct.expiration_timeframe,
        12
      );
      const LOT_UTC = moment().utc().format("YYYY-MM-DD HH:mm:ss");
      const LOT_UTC_Expiration = moment()
        .utc()
        .add(expirationTimeframe, "months")
        .format("YYYY-MM-DD HH:mm:ss");
      const LOT_Local_Time = moment().format("YYYY-MM-DD HH:mm:ss");
      const LOT_Name = `${email}_${moment().format("YYYYMMDD_HH:mm:ss")}`;

      const resp = await axios.post(
        API_R3API_ODOO_EXECUTE_URI,
        {
          action: "createquotation",
          domain: process.env.REACT_APP_DOMAIN_PARAM,
          emailid: email,
          company: false,
          input: {
            product_id: selectedProduct.id,
            product_category: selectedProduct.product_category,
            quantity: selectedProduct.number_of_tokens,
            paidAmount: total,
            unit_of_measure: selectedProduct.unit_of_measure || selectedProduct.uom_name,
            contained_quanity: 1,
            total_quantity: selectedProduct.number_of_tokens,
            LOT_UTC,
            LOT_UTC_Expiration,
            LOT_Local_Time,
            LOT_Name,
            description: paypalOrder
          }
        },
        { headers }
      );

      if (resp.data?.return_code == 0) {
        if (resp.data?.invoice?.bucket_name) {
          localStorage.setItem("bucket", resp.data.invoice.bucket_name);
        }
        if (resp.data?.outputfilepath) {
          localStorage.setItem("outputfilepath", resp.data.outputfilepath);
        }
        setSuccess(
          t("Subscription activated successfully. Included tokens have been credited."),
          true
        );
        navigate("/home");
      } else {
        setError(
          resp.data?.message || t("Payment captured, but subscription activation failed.")
        );
      }
    } catch (error) {
      console.error("Subscription createquotation failed:", error.message);
      setError(error.message || t("Payment captured, but subscription activation failed."));
    } finally {
      setPaymentBusy(false);
    }
  };

  useEffect(() => {
    if (paypalResponse && paypalResponse.status === "COMPLETED") {
      sendTransactionDetails(paypalResponse);
    }
  }, [paypalResponse]);

  const selectProductById = (productId) => {
    if (productId == null || productId === "") {
      userTouchedProductRef.current = false;
      setSelectedProduct(null);
      applySelectedProductPricing(null);
      return;
    }

    userTouchedProductRef.current = true;
    const product = findProductById(productsRef.current, productId);
    setSelectedProduct(product);
    applySelectedProductPricing(product);
  };

  const taxRate = useMemo(
    () => (selectedProduct ? readNumericValue(selectedProduct.tax_amount, 0) : 0),
    [selectedProduct]
  );

  const unitOfMeasure =
    selectedProduct?.unit_of_measure || selectedProduct?.uom_name || "";

  const fetchProducts = async (email, headers, preferredProductId = null) => {
    const resp = await axios.post(
      API_R3API_ODOO_EXECUTE_URI,
      {
        action: "getproductlist",
        domain: process.env.REACT_APP_DOMAIN_PARAM,
        emailid: email,
        company: false,
        companyname: "",
        region: ""
      },
      { headers }
    );

    const productList = extractProducts(resp.data).filter(
      (product) => product?.product_category === SUBSCRIPTION_CATEGORY
    );
    productsRef.current = productList;
    setProducts(productList);

    const targetId = userTouchedProductRef.current
      ? selectedProductIdRef.current
      : preferredProductId ?? selectedProductIdRef.current;

    const nextProduct = findProductById(productList, targetId) || productList[0] || null;
    setSelectedProduct(nextProduct);
    applySelectedProductPricing(nextProduct);

    console.log("Subscription products", productList, "raw:", resp.data);
    return productList;
  };

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;

    const loadInitialData = async () => {
      if (persistedUi.hasCache) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const { email, headers } = await getAuthRequestContext();
        const preferredProductId = selectedProduct?.id ?? persistedUi.selectedProductId;
        await fetchProducts(email, headers, preferredProductId);
      } catch (error) {
        console.error("fetchSubscriptionProducts failed:", error.message);
        if (!cancelled) {
          setError(error.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    loadInitialData();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const canPurchase = canPurchaseSubscription(validityStatus);
  const purchaseBlocked =
    validityChecking ||
    validityStatus == null ||
    validityStatus === STATUS_ERROR ||
    validityStatus === STATUS_ACTIVE;
  const paymentDisabledMessage =
    validityStatus === STATUS_ACTIVE
      ? t(
          "You cannot purchase another subscription until the current subscription expires."
        )
      : validityError || t("Unable to verify your subscription status. Please try again.");

  return (
    <DashboardLayout>
      <div className="dashboard-page get-token-page">
        <DashboardTopbar title={t("Subscription")} tokenBalance={tokenBalance} />

        <div className="page-content">
          <div className="page-header">
            <h1>{t("Yearly Subscription")}</h1>
            <p>
              {t(
                "Purchase or renew your yearly subscription and receive included free tokens."
              )}
            </p>
            {refreshing || paymentBusy || validityChecking ? (
              <p className="dashboard-loading" style={{ marginTop: 8 }}>
                {paymentBusy
                  ? t("Activating subscription...")
                  : validityChecking
                    ? t("Checking subscription status...")
                    : t("Fetching...")}
              </p>
            ) : null}
          </div>

          <div className="get-token-layout">
            <div>
              <SubscriptionStatusCard
                validityChecking={validityChecking}
                validityStatus={validityStatus}
                expirationDate={expirationDate}
                validityError={validityError}
                selectedProduct={selectedProduct}
                canPurchase={canPurchase}
              />

              {loading && products.length === 0 ? (
                <div className="dashboard-loading" style={{ marginBottom: 16 }}>
                  {t("Fetching...")}
                </div>
              ) : null}

              {canPurchase ? (
                <>
                  <SubscriptionProductSelect
                    products={products}
                    selectedProductId={
                      selectedProduct ? normalizeProductId(selectedProduct.id) : ""
                    }
                    onSelectProduct={selectProductById}
                    disabled={loading || paymentBusy || validityChecking}
                  />

                  <SubscriptionOrderDetails
                    unitOfMeasure={unitOfMeasure}
                    productPrice={productPrice}
                    includedTokens={includedTokens}
                    productTax={productTax}
                    totalAmountWithoutTax={totalAmountWithoutTax}
                    total={total}
                  />
                </>
              ) : null}
            </div>

            <div className="get-token-payment-col">
              <TokenPaymentPanel
                selectedProduct={selectedProduct}
                totalSelectedContainedQty={includedTokens}
                totalAmountWithoutTax={totalAmountWithoutTax}
                productTax={productTax}
                total={total}
                taxRate={taxRate}
                paypalResponse={paypalResponse}
                setPaypalResponse={setPaypalResponse}
                loading={loading}
                paymentQty={1}
                tokensLabel={t("Included tokens")}
                paymentDisabled={purchaseBlocked}
                paymentDisabledMessage={paymentDisabledMessage}
                validityChecking={validityChecking}
              />
              {canPurchase ? (
                <TokenAllPlans
                  products={products}
                  selectedProductId={selectedProduct?.id}
                  onSelectProduct={selectProductById}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Subscription;
